import { useCallback, useEffect, useMemo, useState } from "react";

import type { SqliteColumnDef, TypeInfo } from "@moment/api-collab/differential/schema/index";
import { type DatabaseTables, type TableName } from "@moment/api-collab/differential/types";
import { type V1ViewColumnDef as ViewColumnDef } from "@moment/api-collab/differential/v1-base-spec";

import { useCellServices } from "../CellServicesContext";
import { type BaseViewHandle } from "./BaseViewHandle";
import { useColumnsPopoverStore } from "./ColumnsPopover/useColumnsPopoverStore";
import { useBaseViewQuery } from "./useBaseViewQuery";
import { useConfirmColumnTypeChange } from "./useConfirmColumnTypeChange";
import { useDeleteColumn } from "./useDeleteColumn";
import { useHydratedColumnDefs } from "./useHydratedColumnDefs";
import { type HydratedRow, useHydratedRows } from "./useHydratedRows";

// Add word list for random table names
const words: string[] = [
	"cosmic",
	"stellar",
	"lunar",
	"solar",
	"nebula",
	"galaxy",
	"quasar",
	"pulsar",
	"comet",
	"meteor",
	"orbit",
	"planet",
	"star",
	"nova",
	"cosmos",
	"celestial",
	"astral",
	"eclipse",
	"aurora",
	"zenith",
	"vortex",
	"photon",
	"quantum",
	"neutron",
	"plasma",
	"fusion",
	"gravity",
	"vector",
	"matrix",
	"prism",
	"crystal",
	"spectrum",
	"infinity",
	"eternal",
	"void",
	"abyss",
	"horizon",
	"apex",
	"vertex",
];

const generateRandomTableName = () => {
	const randomWords: string[] = [];
	for (let i = 0; i < 4; i++) {
		const randomIndex = Math.floor(Math.random() * words.length);
		randomWords.push(words[randomIndex] ?? "default");
	}
	return randomWords.join("_");
};

export const useBaseView = (viewId: string): BaseViewHandle => {
	const { base, currentUser } = useCellServices();
	const { tableSpec, viewSpec, results: data } = useBaseViewQuery(viewId);
	const table = viewSpec?.query.spec[0].table;
	const isStoreOwner = base?.isStoreOwner ?? false;

	// `instanceKey` is used to identify unique instances of the `<BaseView />` component. Note that
	// this is *NOT* the `cellId`, since the same `cellId` can be used to create multiple views, and
	// it is *ALSO NOT* the `viewId`, since the same `viewId` can be used to create multiple
	// instances of the `<BaseView />` component.
	const uiInstanceKey = useMemo(() => crypto.randomUUID(), []);
	const { popoverOpen, setPopoverOpen } = useColumnsPopoverStore(uiInstanceKey);

	const hydratedColumnDefs = useHydratedColumnDefs(tableSpec, viewSpec);
	const hydratedRows = useHydratedRows(
		data,
		viewSpec?.query,
		hydratedColumnDefs,
		base,
		currentUser?.id ?? undefined
	);

	const [openItemIndex, setOpenItemIndex] = useState<number | undefined>(undefined);
	const openItem = openItemIndex !== undefined ? hydratedRows[openItemIndex] : undefined;

	// Track when we're adding a new row to open its dialog
	const [pendingNewRowId, setPendingNewRowId] = useState<string | undefined>(undefined);

	const closeRowDialog = useCallback(() => setOpenItemIndex(undefined), []);

	// Effect to open dialog for newly added rows
	useEffect(() => {
		if (!pendingNewRowId) return;

		const newRowIndex = data.findIndex(
			(row: unknown) => (row as { id?: string })?.id === pendingNewRowId
		);
		if (newRowIndex !== -1) {
			setOpenItemIndex(newRowIndex);
			setPendingNewRowId(undefined);
		}
	}, [data, pendingNewRowId]);

	const createBlankView = useCallback(async () => {
		if (!base) {
			throw new Error("Base not found");
		}

		const fakeData = [
			{
				id: "1",
				title: "Card 1",
				person: {
					id: "1",
					name: "Alice",
					email: "alice@example.com",
				},
				description: "Description for card 1",
			},
			{
				id: "2",
				title: "Card 2",
				person: {
					id: "2",
					name: "Bob",
					email: "bob@example.com",
				},
				description: "Description for card 3",
			},
			{
				id: "3",
				title: "Card 3",
				person: {
					id: "3",
					name: "Charlie",
					email: "charlie@example.com",
				},
				description: "Description for card 3",
			},
		];

		const table = generateRandomTableName();
		await base.createViewIfNotExists(viewId, table, {
			columns: [
				{ name: "id", type: "TEXT", header: "ID", primaryKey: true },
				{ name: "title", type: "TEXT", header: "Title" },
				{
					name: "person",
					type: "TEXT",
					typeInfo: { enrichedType: "Person", defaultValue: "Empty" },
					header: "Person",
				},
				{ name: "description", type: "TEXT", header: "Description" },
			],
			permissions: [],
		});

		for (const item of fakeData) {
			await base.insert(table, item.id, item);
		}
	}, [base, viewId]);

	const createViewFromTable = useCallback(
		async (table: TableName<DatabaseTables>) => {
			if (!base) {
				throw new Error("Base not found");
			}

			// const viewSpec = base.from(table).spec;
			await base.createViewFromTable(viewId, table);
		},
		[base, viewId]
	);

	const updateViewTitle = useCallback(
		(title: string) => {
			if (!viewSpec) {
				return;
			}
			void base?.updateViewTitle(viewId, title);
		},
		[viewSpec, base, viewId]
	);

	const renameColumn = useCallback(
		(oldColumnName: string, newHeader: string, newColumnName: string) =>
			table && void base?.renameColumn(table, oldColumnName, newHeader, newColumnName),
		[base, table]
	);

	const updateColumn = useCallback(
		async (columnName: string, column: Partial<Omit<SqliteColumnDef, "name" | "header">>) =>
			await base?.updateColumn(table, columnName, column),
		[table, base]
	);

	const columnTypeChangeConfirmation = useConfirmColumnTypeChange();
	const updateColumnType = useCallback(
		async (columnName: string, oldTypeInfo: TypeInfo, newTypeInfo: TypeInfo) => {
			const popoverStateToRestore = popoverOpen;

			// Close the popover while confirmation modal is open.
			setPopoverOpen(undefined);

			const confirmed = await columnTypeChangeConfirmation({
				toType: newTypeInfo.enrichedType,
			});

			// Restore the popover state after closing.
			setPopoverOpen(popoverStateToRestore);

			if (!confirmed) {
				return;
			}
			await base?.updateColumnType(table, columnName, oldTypeInfo, newTypeInfo);
		},
		[table, base, popoverOpen]
	);

	const updateColumnDisplayOptions = useCallback(
		(columnName: string, displayOptions: Partial<Omit<ViewColumnDef, "linkedColumn">>) =>
			base?.updateColumnDisplayOptions(viewId, columnName, displayOptions),
		[base, viewId]
	);

	const appendColumn = useCallback(
		async () => table && (await base?.appendColumn(table)),
		[base, table]
	);

	const deleteColumnConfirmation = useDeleteColumn();
	const deleteColumn = useCallback(
		async (columnName: string) => {
			const confirmed = await deleteColumnConfirmation({ columnName });
			if (!confirmed) {
				return;
			}
			table && (await base?.deleteColumn(table, columnName));
		},
		[base, table, deleteColumnConfirmation]
	);

	const appendRow = useCallback(() => {
		if (!table) {
			throw new Error("Table not found");
		}
		// Close any existing dialog first to prevent state conflicts
		setOpenItemIndex(undefined);

		const id = crypto.randomUUID();
		void base?.insert(table, id, { id });
		// Set pending new row ID to trigger dialog opening when data updates
		setPendingNewRowId(id);
		return id;
	}, [base, table]);

	const deleteRow = useCallback(
		(row: HydratedRow) => {
			if (!table) {
				throw new Error("Table not found");
			}
			// FIXME: We need a notion of a primary key, lol.
			const recordId: string | undefined = row.original?.["id"];
			if (!recordId) {
				throw new Error("Primary key not found");
			}
			closeRowDialog();
			void base?.delete(table, recordId);
		},
		[base, table]
	);

	const openRowDialogAtPrevious = useCallback(() => {
		if (openItemIndex === undefined || openItemIndex === 0) {
			return;
		}
		setOpenItemIndex(openItemIndex - 1);
	}, [openItemIndex]);

	const openRowDialogAtNext = useCallback(() => {
		if (openItemIndex === undefined || openItemIndex === hydratedRows.length - 1) {
			return;
		}
		setOpenItemIndex(openItemIndex + 1);
	}, [openItemIndex, hydratedRows]);

	const canEditRows = useCallback(
		(userId: string | undefined) =>
			table && base?.userHasStoragePermission("writer_own")(table, userId),
		[base, table]
	);

	return useMemo(
		() =>
			({
				uiInstanceKey,
				isStoreOwner,
				spec: viewSpec,
				table,
				data: hydratedRows,
				columns: hydratedColumnDefs,
				createBlankView,
				createViewFromTable,
				updateViewTitle,
				updateColumn,
				updateColumnType,
				updateColumnDisplayOptions,
				renameColumn,
				appendColumn,
				deleteColumn,
				canEditRows,
				appendRow,
				deleteRow,
				rowDialogOpen: openItemIndex !== undefined,
				openItemIndex,
				openItem,
				openRowDialogAtPrevious,
				openRowDialogAtNext,
				openRowDialogAt: setOpenItemIndex,
				closeRowDialog,
			}) satisfies BaseViewHandle,
		[
			uiInstanceKey,
			isStoreOwner,
			viewSpec,
			table,
			hydratedRows,
			hydratedColumnDefs,
			createBlankView,
			createViewFromTable,
			updateViewTitle,
			updateColumn,
			updateColumnType,
			updateColumnDisplayOptions,
			renameColumn,
			appendColumn,
			deleteColumn,
			canEditRows,
			appendRow,
			deleteRow,
			openItemIndex,
			openItem,
			openRowDialogAtPrevious,
			openRowDialogAtNext,
			closeRowDialog,
		]
	);
};
